import React, { useContext, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Dropdown, Tooltip } from 'antd';
import { format } from 'date-fns';
import OpenNotificationWithIcon from 'utils/notification';
import { useAuthPrivilege } from 'hooks/useAuthPrivilege';
import { ADMIN_CACHE } from 'constants/index';
import { FAL } from 'icons/fa';
import { CACHE_RESET } from 'components/cache/cacheResetMutation';
import {
  CACHE_QUICK_CLEARS,
  CACHE_PATTERN_EXAMPLES,
  CacheQuickClear,
  CachePatternExample,
} from 'components/cache/cacheQuickClears';
import {
  CACHE_RECENT_CONTENT,
  CACHE_RECENT_CONTENT_LIMIT,
  NormalizedRecentItem,
  normalizeRecentItems,
} from 'components/cache/cacheRecentContent';
import CacheRecentSkeleton from 'components/cache/CacheRecentSkeleton';
import { useCachePage, NormalizedCachePageInfo } from 'components/cache/cachePageContext';
import { Context as SettingsContext } from 'components/setting/context';
import * as css from './style';

type DropdownView = 'menu' | 'confirmAll';

function buildPageUrl(info: NormalizedCachePageInfo, siteUrlBase?: string): string | null {
  if (info.canonicalUrl) return info.canonicalUrl;
  if (info.canonicalPath && siteUrlBase) return `https://${siteUrlBase}${info.canonicalPath}`;
  return null;
}

const CacheClearDropdown = () => {
  const [clearCache, { loading }] = useMutation(CACHE_RESET);
  const { hasPrivilege } = useAuthPrivilege({ privilege: ADMIN_CACHE });
  const { current: pageInfo } = useCachePage();
  const { settings } = useContext(SettingsContext);
  const siteUrlBase = settings['site.url.base']?.stringValue;

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [view, setView] = useState<DropdownView>('menu');
  const [pattern, setPattern] = useState('');
  const [examplesOpen, setExamplesOpen] = useState(false);

  // Skip the recent-content fetch until the user opens the dropdown so the
  // header doesn't pay for it on every page load. `network-only` keeps the
  // list fresh each open — editors will have just edited something.
  const recentQuery = useQuery(CACHE_RECENT_CONTENT, {
    variables: { perPage: CACHE_RECENT_CONTENT_LIMIT },
    skip: !hasPrivilege || !dropdownVisible,
    fetchPolicy: 'network-only',
  });
  const recentItems = normalizeRecentItems(recentQuery.data?.search);

  function resetState() {
    setView('menu');
    setPattern('');
    setExamplesOpen(false);
  }

  function closeDropdown() {
    setDropdownVisible(false);
    resetState();
  }

  async function handleClearAllConfirmed() {
    closeDropdown();
    try {
      const result = await clearCache({ variables: { scope: null } });
      if (result.data.cacheReset.success) {
        OpenNotificationWithIcon({
          type: 'success',
          title: 'Site cache is now cleared.',
        }, () => {});
        return;
      }
      const errorMsg = result.data.cacheReset.errors?.[0] || 'Unknown error';
      OpenNotificationWithIcon({
        type: 'error',
        title: `Failed to clear site cache: ${errorMsg}`,
      }, () => {});
    } catch {
      OpenNotificationWithIcon({
        type: 'error',
        title: 'There was an issue with clearing the site cache. Try again.',
      }, () => {});
    }
  }

  async function handleClearCurrentPage() {
    if (!pageInfo) return;
    const url = buildPageUrl(pageInfo, siteUrlBase);
    if (!url) return;

    closeDropdown();
    try {
      const result = await clearCache({ variables: { scope: 'urls', urls: [url] } });
      if (result.data.cacheReset.success) {
        OpenNotificationWithIcon({
          type: 'success',
          title: `Cache cleared for ${pageInfo.label || 'this page'}.`,
        }, () => {});
        return;
      }
      const errorMsg = result.data.cacheReset.errors?.[0] || 'Unknown error';
      OpenNotificationWithIcon({
        type: 'error',
        title: `Failed to clear page cache: ${errorMsg}`,
      }, () => {});
    } catch {
      OpenNotificationWithIcon({
        type: 'error',
        title: 'Failed to clear page cache. Try again.',
      }, () => {});
    }
  }

  async function clearByPattern(submittedPattern: string, successLabel: string, errorLabel: string) {
    try {
      const result = await clearCache({
        variables: { scope: 'pattern', urlPattern: submittedPattern },
      });
      if (result.data.cacheReset.success) {
        OpenNotificationWithIcon({
          type: 'success',
          title: successLabel,
        }, () => {});
        return;
      }
      const errorMsg = result.data.cacheReset.errors?.[0] || 'Unknown error';
      OpenNotificationWithIcon({
        type: 'error',
        title: `${errorLabel}: ${errorMsg}`,
      }, () => {});
    } catch {
      OpenNotificationWithIcon({
        type: 'error',
        title: `${errorLabel}. Try again.`,
      }, () => {});
    }
  }

  async function handleClearPattern() {
    const submittedPattern = pattern.trim();
    if (!submittedPattern) return;

    closeDropdown();
    await clearByPattern(
      submittedPattern,
      `Cache cleared for pattern: ${submittedPattern}`,
      'Failed to clear cache by pattern',
    );
  }

  async function handleQuickClear(quick: CacheQuickClear) {
    closeDropdown();
    await clearByPattern(
      quick.pattern,
      `Cache cleared: ${quick.label}`,
      `Failed to clear ${quick.label.toLowerCase()}`,
    );
  }

  function handleUseExample(example: CachePatternExample) {
    setPattern(example.pattern);
  }

  async function handleClearRecentItem(item: NormalizedRecentItem) {
    if (!item.canonicalUrl) return;
    const url = item.canonicalUrl;

    closeDropdown();
    try {
      const result = await clearCache({ variables: { scope: 'urls', urls: [url] } });
      if (result.data.cacheReset.success) {
        OpenNotificationWithIcon({
          type: 'success',
          title: `Cache cleared: ${item.title}`,
        }, () => {});
        return;
      }
      const errorMsg = result.data.cacheReset.errors?.[0] || 'Unknown error';
      OpenNotificationWithIcon({
        type: 'error',
        title: `Failed to clear cache for ${item.title}: ${errorMsg}`,
      }, () => {});
    } catch {
      OpenNotificationWithIcon({
        type: 'error',
        title: `Failed to clear cache for ${item.title}. Try again.`,
      }, () => {});
    }
  }

  function formatRelativeTime(iso: string | null): string {
    if (!iso) return '';
    const ts = Date.parse(iso);
    if (Number.isNaN(ts)) return '';
    const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    // Beyond a week, an absolute date carries more memory than "3w ago".
    // Drop the year unless the edit predates this calendar year.
    const date = new Date(ts);
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return format(date, sameYear ? 'MMM d' : 'MMM d, yyyy');
  }

  const pageUrl = pageInfo ? buildPageUrl(pageInfo, siteUrlBase) : null;
  const pageLabel = pageInfo?.label || 'this page';

  // Homepage is logically a "one page" clear (it targets `/`), not a
  // many-pages rule, so it lives in tier 1 alongside Clear Current Page.
  // The `featured` flag is the routing signal — anything featured renders
  // in tier 1; everything else stays in the tier-2 quick-clears grid.
  const homepageQuick = CACHE_QUICK_CLEARS.find((q) => q.featured);
  const groupedQuickClears = CACHE_QUICK_CLEARS.filter((q) => !q.featured);

  const overlay = (
    <css.CacheDropdownContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      {view === 'menu' && (
        <>
          <css.CacheTier $tone="primary">
            <css.CacheTierLabel>One page</css.CacheTierLabel>

            {(pageUrl || homepageQuick) && (
              <css.CacheOnePageRow>
                {pageUrl && (
                  <css.CacheOnePageButton type="button" onClick={handleClearCurrentPage}>
                    <strong>Clear Current Page</strong>
                    <span>{`Just ${pageLabel}`}</span>
                  </css.CacheOnePageButton>
                )}
                {homepageQuick && (
                  <css.CacheOnePageButton
                    type="button"
                    onClick={() => handleQuickClear(homepageQuick)}
                    $secondary={!!pageUrl}
                  >
                    <strong>{homepageQuick.label}</strong>
                    <span>{homepageQuick.description}</span>
                  </css.CacheOnePageButton>
                )}
              </css.CacheOnePageRow>
            )}

            <css.CacheSubsectionLabel>Recently edited</css.CacheSubsectionLabel>
            {recentQuery.loading && !recentItems.length ? (
              <css.CacheRecentSkeletonWrap>
                <CacheRecentSkeleton rows={4} />
              </css.CacheRecentSkeletonWrap>
            ) : recentItems.length === 0 ? (
              <css.CacheRecentEmpty>Nothing edited recently.</css.CacheRecentEmpty>
            ) : (
              <css.CacheRecentList>
                {recentItems.map((item) => {
                  const disabled = !item.canonicalUrl;
                  return (
                    <css.CacheRecentRow
                      key={item.key}
                      type="button"
                      onClick={() => handleClearRecentItem(item)}
                      disabled={disabled}
                      title={
                        disabled
                          ? 'Draft — nothing cached yet.'
                          : `Clear cache for ${item.canonicalUrl}`
                      }
                    >
                      <css.CacheRecentKind $kind={item.kind}>
                        {item.kindLabel}
                      </css.CacheRecentKind>
                      <css.CacheRecentTitle>{item.title}</css.CacheRecentTitle>
                      <css.CacheRecentTime>
                        {disabled ? 'Not yet published' : formatRelativeTime(item.updatedAt)}
                      </css.CacheRecentTime>
                    </css.CacheRecentRow>
                  );
                })}
              </css.CacheRecentList>
            )}
          </css.CacheTier>

          <css.CacheTier $tone="secondary">
            <css.CacheTierLabel>Many pages</css.CacheTierLabel>

            <css.CacheQuickClearsGrid>
              {groupedQuickClears.map((quick) => (
                <css.CacheQuickClearChip
                  key={quick.id}
                  type="button"
                  onClick={() => handleQuickClear(quick)}
                >
                  <strong>{quick.label}</strong>
                  <span>{quick.description}</span>
                </css.CacheQuickClearChip>
              ))}
            </css.CacheQuickClearsGrid>

            <css.CacheSubsectionLabel>By URL pattern</css.CacheSubsectionLabel>
            <css.CachePatternForm>
              <css.CachePatternInputRow>
                <input
                  type="text"
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  placeholder="e.g. articles in a single topic"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClearPattern();
                  }}
                  aria-label="URL pattern"
                />
                <button
                  type="button"
                  onClick={handleClearPattern}
                  disabled={!pattern.trim() || loading}
                >
                  Clear
                </button>
              </css.CachePatternInputRow>

              <css.CacheExamplesToggle
                type="button"
                onClick={() => setExamplesOpen((open) => !open)}
                aria-expanded={examplesOpen}
              >
                <css.CacheExamplesCaret $open={examplesOpen}>
                  <FAL icon="angle-right" />
                </css.CacheExamplesCaret>
                {examplesOpen ? 'Hide pattern examples' : 'Show pattern examples'}
              </css.CacheExamplesToggle>

              {examplesOpen && (
                <css.CacheExamplesPanel>
                  <css.CacheExamplesIntro>
                    Patterns match the URL path only. Click an example to fill the box,
                    then replace the placeholder slugs with your real path.
                  </css.CacheExamplesIntro>
                  {CACHE_PATTERN_EXAMPLES.map((example) => (
                    <css.CacheExampleRow
                      key={example.pattern}
                      type="button"
                      onClick={() => handleUseExample(example)}
                    >
                      <css.CacheExampleLabel>{example.label}</css.CacheExampleLabel>
                      <css.CacheExamplePattern>{example.pattern}</css.CacheExamplePattern>
                    </css.CacheExampleRow>
                  ))}
                </css.CacheExamplesPanel>
              )}
            </css.CachePatternForm>
          </css.CacheTier>

          <css.CacheClearAllLink type="button" onClick={() => setView('confirmAll')}>
            Clear entire site cache
          </css.CacheClearAllLink>
        </>
      )}

      {view === 'confirmAll' && (
        <css.CacheConfirmPanel>
          <css.CacheConfirmTitle>
            <FAL icon="exclamation-triangle" /> Clear entire site cache?
          </css.CacheConfirmTitle>
          <css.CacheConfirmBody>
            This invalidates every cached page across Varnish and Cloudflare and
            rotates the Memcached metakey. <strong>Site speed will noticeably
            degrade for visitors</strong> until the caches repopulate. Prefer
            "Clear Current Page" or "Clear by URL Pattern" when possible.
          </css.CacheConfirmBody>
          <css.CacheConfirmActions>
            <button type="button" onClick={resetState}>Cancel</button>
            <button type="button" className="danger" onClick={handleClearAllConfirmed} disabled={loading}>
              Yes, clear everything
            </button>
          </css.CacheConfirmActions>
        </css.CacheConfirmPanel>
      )}
    </css.CacheDropdownContent>
  );

  return (
    <css.CacheButton>
      <Tooltip title={hasPrivilege ? 'Clear site or page-specific cache' : ''}>
        <Dropdown
          overlay={overlay}
          trigger={['click']}
          visible={dropdownVisible}
          onVisibleChange={(v) => {
            setDropdownVisible(v);
            if (!v) resetState();
          }}
          disabled={!hasPrivilege}
        >
          <button disabled={!hasPrivilege || loading}>
            {loading ? 'Clearing...' : 'Clear Cache'}
            <css.CacheCaret>
              <FAL icon="angle-down" />
            </css.CacheCaret>
          </button>
        </Dropdown>
      </Tooltip>
    </css.CacheButton>
  );
};

export default CacheClearDropdown;
